import { Link } from "react-router-dom";
import Layout, { Breadcrumb } from "@/components/feature/Layout";
import { usePageMeta } from "@/hooks/usePageMeta";
import { comicApps } from "@/mocks/apps";

const values = [
  {
    icon: "ri-eye-line",
    title: "真实体验",
    desc: "每一款漫画软件应用都经过我们逐款上手实测，用真实阅读体验说话。",
  },
  {
    icon: "ri-compass-3-line",
    title: "客观中立",
    desc: "榜单与评测不偏袒、不吹捧，优缺点一并呈现，帮你理性决策。",
  },
  {
    icon: "ri-refresh-line",
    title: "持续更新",
    desc: "漫画软件应用层出不穷，我们持续跟进新应用与功能变化，保持内容新鲜。",
  },
  {
    icon: "ri-user-heart-line",
    title: "读者为本",
    desc: "一切内容围绕你的真实需求展开，让你花最少时间选对工具。",
  },
];

const testimonials = [
  {
    name: "小鹿",
    role: "国漫爱好者",
    text: "按榜单装了快看漫画，签到换阅读券这个点真的香，追更再也不用纠结充会员了。",
  },
  {
    name: "阿杰",
    role: "日漫党",
    text: "一直纠结正版日漫去哪看，看了哔哩哔哩漫画的评测后果断入坑，画质和更新都很稳。",
  },
  {
    name: "橘子",
    role: "韩漫迷",
    text: "被咚漫的韩漫专题安利到了，条漫画风真的好看，更新也快，已经追了七八部。",
  },
];

export default function About() {
  usePageMeta({
    title: "关于漫选社 - 我们是谁",
    description:
      "漫选社是一群重度漫画读者组成的团队，坚持从真实体验出发评测主流漫画软件应用，整理成排行与推荐，帮读者快速选对好用的漫画软件应用。",
    canonical: "https://mangao0.github.io/about",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "关于漫选社",
      description: "专注漫画软件应用评测与推荐的内容团队",
      url: "https://mangao0.github.io/about",
    },
  });

  return (
    <Layout title="关于我们">
      <Breadcrumb crumbs={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />

      <div className="mx-auto w-full max-w-6xl px-4 pb-16 pt-4 md:px-6">
        <div className="overflow-hidden rounded-3xl border border-background-200/80 bg-background-100">
          <div className="relative h-56 w-full md:h-72">
            <img
              src="https://readdy.ai/api/search-image?query=A%20warm%20stylized%20illustration%20of%20a%20team%20of%20people%20reading%20comics%20together%20around%20a%20table%20with%20books%20and%20tablets%2C%20cozy%20coral%20amber%20and%20teal%20palette%2C%20friendly%20community%20feeling%2C%20clean%20flat%20illustration%20style%2C%20warm%20inviting%20atmosphere&width=1400&height=520&seq=about-banner&orientation=landscape"
              alt="漫选社团队一起看漫画的插画"
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background-950/70 to-transparent"></div>
            <h1 className="absolute bottom-6 left-6 font-heading text-2xl text-background-50 md:left-8 md:text-4xl">一群爱看漫画的人，认真帮你选应用</h1>
          </div>

          <div className="p-6 md:p-10">
            <h2 className="text-xl font-bold text-foreground-950">我们是谁</h2>
            <p className="mt-3 text-base leading-relaxed text-foreground-700">
              漫选社是一群重度漫画读者。我们踩过不少找漫画软件应用的坑——要么资源少、要么广告多、要么画质差。为了不再踩坑，也为了帮更多同样爱看漫画的人节省挑选时间，我们成立了漫选社。
            </p>
            <p className="mt-3 text-base leading-relaxed text-foreground-700">
              我们把重心放在两件事上：一是逐款上手评测主流的漫画软件应用，把真实体验讲清楚；二是把评测整理成清晰易读的排行与推荐，让你在几分钟内锁定最适合自己的那一款。
            </p>
          </div>
        </div>

        <div className="mt-10">
          <h2 className="text-center text-xl font-bold text-foreground-950 md:text-2xl">我们的坚持</h2>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {values.map((v) => (
              <div key={v.title} className="rounded-2xl border border-background-200/80 bg-background-100 p-6 transition-colors hover:border-primary-300">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent-100 text-accent-700">
                  <i className={`${v.icon} text-xl`} aria-hidden="true"></i>
                </span>
                <h3 className="mt-4 text-base font-bold text-foreground-950">{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-700">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-2">
          <div className="rounded-2xl border border-background-200/80 bg-background-100 p-6 md:p-8">
            <h2 className="text-xl font-bold text-foreground-950">读者怎么说</h2>
            <div className="mt-5 space-y-4">
              {testimonials.map((t) => (
                <figure key={t.name} className="rounded-2xl bg-background-50 p-5">
                  <div className="flex items-center gap-2 text-secondary-500" aria-label="五颗星好评">
                    {[1, 2, 3, 4, 5].map((i) => <i key={i} className="ri-star-fill" aria-hidden="true"></i>)}
                  </div>
                  <blockquote className="mt-3 text-sm leading-relaxed text-foreground-700">“{t.text}”</blockquote>
                  <figcaption className="mt-3 text-xs text-foreground-500">
                    <span className="font-bold text-foreground-800">{t.name}</span> · {t.role}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-background-200/80 bg-background-100 p-6 md:p-8">
            <h2 className="text-xl font-bold text-foreground-950">我们正在评测的应用</h2>
            <p className="mt-2 text-sm text-foreground-600">榜单持续更新中，欢迎随时查看最新漫画软件应用排行。</p>
            <div className="mt-5 flex flex-wrap gap-2">
              {comicApps.map((a) => (
                <Link key={a.id} to={`/reviews/${a.id}`} className="whitespace-nowrap rounded-full bg-background-50 px-3 py-1.5 text-sm text-foreground-700 transition-colors hover:bg-primary-500 hover:text-background-50">
                  {a.name}
                </Link>
              ))}
            </div>
            <div className="mt-6 rounded-2xl bg-accent-100/70 p-5">
              <h3 className="text-base font-bold text-foreground-950">想合作或提建议？</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-700">欢迎联系漫选社，无论是应用推荐、内容合作还是使用反馈，我们都很乐意倾听。</p>
              <Link to="/contact" className="mt-4 inline-flex items-center gap-1 whitespace-nowrap rounded-lg bg-primary-500 px-5 py-2.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600">
                联系我们
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}